import React from 'react'
import {
  MapContainer,
  TileLayer,
} from 'react-leaflet'

import 'leaflet.sync'
import withWidth, { isWidthUp } from '@material-ui/core/withWidth'

import { makeStyles } from '@material-ui/core/styles'
import { computeIndex } from '../../../../utils/gradient'
import { useDebounce } from 'usehooks-ts'

import Grid from '@material-ui/core/Grid'
import CircularProgress from '@material-ui/core/CircularProgress'
import { getParcelBounds } from '../../../../utils/map'
import {
  createImageUrlWithDate,
} from '../../../../utils/parcels'
import { createCanvasForParcel } from '../../../../utils/map'
import { useSatelliteImages } from '../../../../context/SatelliteImagesContext'
import { convertArrayToObject } from '../../../../utils'
import { useFeatureUrl } from '../../../../adapters/sentinel'
import { useAuth } from '../../../../context/AuthContext'
import MapLayersManager from '../../MapLayersManager'
import WMSLayer from '../../WMSLayer'
import LayerLegend from './LayerLegend'
import { usePaletteScript, withPaletteScript } from '../../../../adapters/paletteRange'
import { usePaletteAdjustmentsEnabled } from '../../../../context/PaletteAdjustmentsContext'
import { rescaleLegend } from '../../../../utils/palette'

// Center map on Granada
const position = [37.741934, -3.188515]

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  loading: {
    marginBottom: theme.spacing(20),
    marginTop: theme.spacing(20),
  },
}))

function LittleWMSMap({
  parcel,
  scrData,
  map,
  setMap,
  layer,
  selectedResolution,
  baseRef,
  comparedRef,
  farms,
  width,
  height = 400,
}) {
  const classes = useStyles()
  const [canvas, setCanvas] = React.useState(null)
  const [canvasLoading, setCanvasLoading] = React.useState(true)
  const [colorPointer, setColorPointer] = React.useState(null)
  const [featureUrl, setFeatureUrl] = React.useState(null)
  const debouncedFeatureUrl = useDebounce(featureUrl, 300)
  const { loggedUser } = useAuth()
  const { satelliteImages, dispatchSatelliteImages } = useSatelliteImages()

  // The user's saved palette adjustment for this farm/layer, so the comparator renders
  // the same colours as the main map (only in apps that enable palette adjustments)
  const paletteEnabled = usePaletteAdjustmentsEnabled()
  const { data: palette, isLoading: paletteLoading } = usePaletteScript(
    { parcelId: parcel?.id, layerKey: layer?.key, date: scrData?.date },
    { enabled: paletteEnabled }
  )
  const paletteUrl = palette?.evalscript_url || null
  const legendLayer = React.useMemo(
    () =>
      paletteUrl
        ? { ...layer, legend: rescaleLegend(layer.legend, palette) }
        : layer,
    [layer, paletteUrl, palette?.min, palette?.max]
  )
  const { data, isLoading, isError, refetch } = useFeatureUrl(
    debouncedFeatureUrl?.url
  )

  const index = React.useMemo(
    () =>
      colorPointer
        ? computeIndex(colorPointer, legendLayer.legend, scrData?.basicStats)
        : null,
    [colorPointer, legendLayer]
  )

  React.useEffect(() => {
    if (canvas) {
      if (!(canvas.imageUrl in satelliteImages))
        dispatchSatelliteImages({
          type: 'ADD_IMAGES',
          payload: convertArrayToObject([canvas], 'parcelUrl'),
        })
    }
  }, [canvas])
  
  React.useEffect(() => {
    if (canvas) {
      setCanvas(null)
      setCanvasLoading(true)
    }
  }, [parcel, scrData, paletteUrl])

  React.useEffect(() => {
    if (scrData?.date && !paletteLoading) {
      const imageUrl = withPaletteScript(
        createImageUrlWithDate(parcel, layer, scrData.date, selectedResolution),
        palette
      )

      if (`${imageUrl},${parcel.id}` in satelliteImages) {
        setCanvasLoading(false)
        setCanvas(satelliteImages[`${imageUrl},${parcel.id}`])
      } else {
        const parcelCanvas = createCanvasForParcel(
          parcel,
          imageUrl,
          selectedResolution,
          layer,
          () => {
            setCanvasLoading(false)
          }
        )
        setCanvas(parcelCanvas)
      }
    }
  }, [layer, scrData, paletteLoading, paletteUrl])

  React.useEffect(() => {
    if (map) {
      try {
        map.fitBounds(getParcelBounds(parcel))
      } catch (error) {
        console.error(error)
      }
    }
  }, [map, scrData, parcel])

  React.useEffect(() => {
    const indexValue =
      data?.features?.length > 0
        ? data?.features[0]?.properties?.out1
        : undefined
    const result = indexValue
      ? indexValue.substring(0, indexValue.indexOf('.') + 3)
      : undefined
    if (result && debouncedFeatureUrl?.latlng) {
      baseRef.current
        .bindTooltip(
          `Value: <b style="background: ${index?.color}; color: 'black'; font-size:16px">${result}</b><br />`
        )
        .openTooltip(debouncedFeatureUrl?.latlng)
    } else {
      baseRef.current?.unbindTooltip()
    }
  }, [data])

  function transform(anchor_width, anchor_height, x, y) {
    return [
      (x / anchor_width) * canvas.canvas.width,
      (y / anchor_height) * canvas.canvas.height,
    ]
  }

  return (
    <>
      {canvas && !canvasLoading && scrData?.date ? (
        <MapContainer
          center={position}
          minZoom={2}
          maxZoom={24}
          zoom={9}
          style={{ width: '100%', height, minHeight: 360, borderRadius: 4 }}
          zoomControl={false}
          whenCreated={setMap}
        >
          {!isWidthUp('sm', width) && (
            <TileLayer
              maxZoom={24}
              maxNativeZoom={20}
              attribution="Maps Hybrid"
              url={`https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}`}
            />
          )}
          <MapLayersManager
            companyId={loggedUser?.company?.id}
            map={map}
            width={width}
            openAddParcel={{ open: false }}
            noAppBar
            controlTop={12}
            controlRight={12}
            forceSimpleControl
            hideOverlays
          />
          {!layer.is_sentinel && (
            <WMSLayer
              key={layer.name}
              layer={layer}
              farms={farms}
              date={scrData?.date}
              selectedResolution={selectedResolution}
              parcel={parcel}
            />
          )}
          <LayerLegend layer={legendLayer} customRange={!!paletteUrl} />
        </MapContainer>
      ) : (
        <Grid item xs={3} align="center">
          <CircularProgress className={classes.loading} />
        </Grid>
      )}
    </>
  )
}

export default withWidth()(LittleWMSMap)
